import { InfoCircleOutlined } from '@ant-design/icons';
import { Button, Form, Input, Modal, Select, Spin } from 'antd';
import type { RuleObject } from 'antd/es/form';
import { useContext, useEffect, useMemo } from 'react';

import { FormLineLayout } from 'app/components/commons/Form/FormLineLayout/FormLineLayout';
import { HotelContext } from 'app/context/HotelContext/HotelContext';
import {
  ViewEditModeContext,
  ViewEditModeContextProvider,
} from 'app/context/ViewEditModeContext/ViewEditModeContext';
import { useHotelFinance } from 'app/hooks/data/core/extranet/hotel/useHotelFinance';
import { useSaveHotelFinance } from 'app/hooks/data/core/extranet/hotel/useSaveHotelFinance';
import { useCountries } from 'app/hooks/data/useCountries';
import type { ExtranetHotelFinanceForm } from 'app/typings/core/extranet/hotel/finance';
import type { BillingInformationSource } from 'app/typings/track/trackingProperty';
import { amplitudeTrack } from 'app/utils/track/amplitude';
import { generateVatMessage, generateVatValidator } from 'app/utils/vat';

import './BillingInformationModal.scss';
import { normalizeTaxIdentifier } from './utils';

const FRANCE_COUNTRY_ID = 1;
const E_INVOICING_ID_DESCRIPTION =
  'The ID used to receive your electronic invoices.';

type BillingInformationModalProps = {
  isOpen: boolean;
  source: BillingInformationSource;
  onClose: () => void;
  onSaved?: () => void;
};

const BillingInformationForm = ({
  isOpen,
  source,
  onClose,
  onSaved,
}: BillingInformationModalProps) => {
  const [form] = Form.useForm<ExtranetHotelFinanceForm>();
  const { hotelId } = useContext(HotelContext);
  const { enterEditMode } = useContext(ViewEditModeContext);
  const { data: countries } = useCountries();
  const { data: finance } = useHotelFinance(hotelId);
  const { mutateAsync: saveFinance, isLoading: isSaving } =
    useSaveHotelFinance(hotelId);

  const formValues = Form.useWatch([], form);
  const countryValue = formValues?.country;

  const initialValues = useMemo(
    () => ({
      companyName: finance?.companyName ?? undefined,
      street: finance?.street ?? undefined,
      zipCode: finance?.zipCode ?? undefined,
      city: finance?.city ?? undefined,
      country: finance?.country ?? undefined,
      vat: normalizeTaxIdentifier(finance?.vat),
      siret: normalizeTaxIdentifier(finance?.siret),
      eInvoicingId: finance?.eInvoicingId ?? undefined,
    }),
    [finance]
  );

  const sortedCountries = useMemo(
    () =>
      countries
        ? [...countries].sort((a, b) => a.name.localeCompare(b.name))
        : [],
    [countries]
  );

  const selectedCountry = countryValue
    ? countries?.find((country) => country.name === countryValue)
    : countries?.find((country) => country.id === finance?.countryId);

  const isFrenchBillingCountry = selectedCountry?.id === FRANCE_COUNTRY_ID;

  const hasChanges = useMemo(
    () =>
      !!formValues &&
      (Object.keys(formValues) as (keyof ExtranetHotelFinanceForm)[]).some(
        (field) => (formValues[field] ?? '') !== (initialValues[field] ?? '')
      ),
    [formValues, initialValues]
  );

  const vatValidator = useMemo(
    () => generateVatValidator(selectedCountry?.slug, false),
    [selectedCountry?.slug]
  );
  const vatMessage = useMemo(
    () => generateVatMessage(selectedCountry?.slug),
    [selectedCountry?.slug]
  );

  const siretValidator = (_: RuleObject, value?: string) => {
    if (!value || /^\d{14}$/.test(value)) {
      return Promise.resolve();
    }

    return Promise.reject('SIRET must be 14 digits');
  };

  useEffect(() => {
    enterEditMode();
  }, [enterEditMode]);

  useEffect(() => {
    if (isOpen) {
      amplitudeTrack('Open billing information', { from: source });
    }
  }, [isOpen, source]);

  useEffect(() => {
    if (selectedCountry && !isFrenchBillingCountry) {
      form.setFieldsValue({ siret: undefined, eInvoicingId: undefined });
    }
  }, [selectedCountry, isFrenchBillingCountry, form]);

  const handleCancel = () => {
    amplitudeTrack('Cancel billing information');
    onClose();
  };

  const onFinish = async (values: ExtranetHotelFinanceForm) => {
    try {
      await saveFinance(values);
    } catch (error) {
      return;
    }

    amplitudeTrack('Save billing information');
    onSaved?.();
    onClose();
  };

  return (
    <Modal
      width={680}
      className="billingInformationModal"
      open={isOpen}
      title="Billing information"
      onCancel={handleCancel}
      centered
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          loading={isSaving}
          disabled={!finance || !hasChanges}
          onClick={() => form.submit()}
        >
          Save
        </Button>,
      ]}
    >
      {finance ? (
        <Form
          form={form}
          onFinish={onFinish}
          preserve={false}
          initialValues={initialValues}
        >
          <div className="billingInformationModal__body">
            <FormLineLayout label="Company name" isInModal isRequired>
              <Form.Item
                name="companyName"
                rules={[{ required: true, message: 'This field is required' }]}
              >
                <Input placeholder="Company name" />
              </Form.Item>
            </FormLineLayout>
            <FormLineLayout label="Street" isInModal>
              <Form.Item name="street">
                <Input placeholder="Street address" />
              </Form.Item>
            </FormLineLayout>
            <FormLineLayout label="ZIP" isInModal>
              <Form.Item name="zipCode">
                <Input placeholder="ZIP code" />
              </Form.Item>
            </FormLineLayout>
            <FormLineLayout label="City" isInModal>
              <Form.Item name="city">
                <Input placeholder="City" />
              </Form.Item>
            </FormLineLayout>
            <FormLineLayout label="Country" isInModal>
              <Form.Item name="country">
                <Select placeholder="Select country" showSearch>
                  {sortedCountries.map((country) => (
                    <Select.Option key={country.id} value={country.name}>
                      {country.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </FormLineLayout>
            <FormLineLayout label="VAT number" isInModal isRequired>
              <Form.Item
                name="vat"
                validateFirst
                rules={[
                  { required: true, message: 'This field is required' },
                  { message: vatMessage, validator: vatValidator },
                ]}
                getValueFromEvent={(e: React.ChangeEvent<HTMLInputElement>) =>
                  normalizeTaxIdentifier(e.target.value)
                }
              >
                <Input placeholder="VAT number" />
              </Form.Item>
            </FormLineLayout>
            {isFrenchBillingCountry && (
              <>
                <FormLineLayout label="SIRET" isInModal isRequired>
                  <Form.Item
                    name="siret"
                    rules={[
                      { required: true, message: 'This field is required' },
                      {
                        message: 'SIRET must be 14 digits',
                        validator: siretValidator,
                      },
                    ]}
                    getValueFromEvent={(
                      e: React.ChangeEvent<HTMLInputElement>
                    ) => normalizeTaxIdentifier(e.target.value)}
                  >
                    <Input placeholder="SIRET" maxLength={14} />
                  </Form.Item>
                </FormLineLayout>
                <FormLineLayout label="E-invoicing ID" isInModal>
                  <Form.Item
                    name="eInvoicingId"
                    extra={
                      <div className="billingInformationModal__description">
                        <InfoCircleOutlined />
                        <div>{E_INVOICING_ID_DESCRIPTION}</div>
                      </div>
                    }
                  >
                    <Input placeholder="Electronic invoicing identifier" />
                  </Form.Item>
                </FormLineLayout>
              </>
            )}
          </div>
        </Form>
      ) : (
        <div className="billingInformationModal__loader">
          <Spin />
        </div>
      )}
    </Modal>
  );
};

export const BillingInformationModal = (
  props: BillingInformationModalProps
) => (
  <ViewEditModeContextProvider>
    <BillingInformationForm {...props} />
  </ViewEditModeContextProvider>
);
